import { useRef, useCallback } from 'react';
import type { Stroke, Point, Camera } from '../types';
import { dist, worldToScreen, getViewportBounds, isInViewport } from '../utils';
import { measureDistance, measurePath } from '../../shared/measureDistance.js';
import type { MeasurementMode, TextObject, TextFontSize } from '@roll1/shared';
import type { CanvasEngine } from './useCanvasEngine';
import { useI18n } from '../i18n';

const TEXT_FONT_SIZE_MAP: Record<TextFontSize, number> = {
  small: 14,
  medium: 20,
  large: 28,
};

export interface PublicRuler {
  by: string;
  points: Point[];
  totalDistance: number;
  /** Timestamp when to auto-hide (ms since epoch). 0 = persistent (no auto-hide). */
  hideAt: number;
  /** Optional color for the ruler line. Defaults to '#ffaa00'. */
  color?: string;
}

/** Offscreen canvas cache for the drawing layer */
interface OffscreenLayerCache {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  valid: boolean;
  width: number;
  height: number;
  /** Camera state when cache was last built — used for transform-based fast redraws */
  cam: Camera;
}

export interface UseDrawingReturn {
  strokes: React.MutableRefObject<Stroke[]>;
  currentStroke: React.MutableRefObject<Stroke | null>;
  color: React.MutableRefObject<string>;
  lineWidth: React.MutableRefObject<number>;
  shapeType: React.MutableRefObject<string>;
  measurementMode: React.MutableRefObject<MeasurementMode>;
  gridSizeFt: React.MutableRefObject<number>;
  /** Measure line points (including live cursor). Empty when not measuring. */
  measurePoints: React.MutableRefObject<Point[]>;
  /** Live token drag ruler points (origin → waypoints → cursor). */
  tokenDragPoints: React.MutableRefObject<Point[]>;
  publicRulers: React.MutableRefObject<PublicRuler[]>;
  /** External ref to text objects — set from AppShell to render text in drawing layer */
  textObjectsRef: React.MutableRefObject<TextObject[]>;
  /** Currently selected text object ID — set from AppShell for selection highlight */
  selectedTextIdRef: React.MutableRefObject<string | null>;
  startStroke: (wx: number, wy: number, tool: string) => void;
  continueStroke: (wx: number, wy: number, tool: string) => void;
  endStroke: () => void;
  startMeasure: (wx: number, wy: number, isPublic?: boolean) => void;
  updateMeasure: (wx: number, wy: number) => void;
  addWaypoint: (wx: number, wy: number) => void;
  endMeasure: () => void;
  /** Start a token drag ruler overlay (shows distance while dragging a token). */
  startTokenDragRuler: (wx: number, wy: number) => void;
  /** Update the token drag ruler as token moves. */
  updateTokenDragRuler: (wx: number, wy: number) => void;
  /** Pin a waypoint in the token drag ruler (Q key) — turns it into a polyline
   * whose distance sums along the segments. */
  addTokenDragWaypoint: (wx: number, wy: number) => void;
  /** End the token drag ruler overlay. */
  endTokenDragRuler: () => void;
  showPublicRuler: (ruler: PublicRuler) => void;
  hidePublicRuler: (by: string) => void;
  /** Dismiss all visible public rulers. */
  clearPublicRulers: () => void;
  clearAll: () => void;
  renderDrawing: () => void;
  /** Invalidate the offscreen drawing cache (call on camera change / resize / page switch) */
  invalidateCache: () => void;
  /** Called when a public ruler should be broadcast. persistent=true means it stays until dismissed. */
  onRulerShow?: (points: Point[], totalDistance: number, persistent?: boolean) => void;
  /** Called when a public ruler should be hidden. */
  onRulerHide?: () => void;
}

export function useDrawing(
  engine: CanvasEngine,
  opts?: {
    onRulerShow?: (points: Point[], totalDistance: number, persistent?: boolean) => void;
    onRulerHide?: () => void;
    onEraseStroke?: (strokeIndex: number, stroke: Stroke) => void;
    measurementMode?: MeasurementMode;
    gridSizeFt?: number;
  },
): UseDrawingReturn {
  const { t } = useI18n();
  const tRef = useRef(t);
  tRef.current = t;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const strokes = useRef<Stroke[]>([]);
  const currentStroke = useRef<Stroke | null>(null);
  const color = useRef('#ff0000');
  const lineWidth = useRef(3);
  const shapeType = useRef('rect');
  const measurementMode = useRef<MeasurementMode>(opts?.measurementMode ?? 'simple');
  const gridSizeFt = useRef<number>(opts?.gridSizeFt ?? 5);
  const publicRulers = useRef<PublicRuler[]>([]);
  const textObjectsRefInternal = useRef<TextObject[]>([]);
  const selectedTextIdRefInternal = useRef<string | null>(null);

  // Keep refs in sync when opts change
  measurementMode.current = opts?.measurementMode ?? 'simple';
  gridSizeFt.current = opts?.gridSizeFt ?? 5;

  /** All waypoints including the live cursor point at the end. */
  const measurePoints = useRef<Point[]>([]);
  /** Whether the current measurement is public (Shift+drag). */
  const measurePublic = useRef(false);
  /** Whether we have already sent a ruler:show to broadcast. */
  const publicRulerSent = useRef(false);

  /** Token drag ruler: start + current end point (2-point path) */
  const tokenDragPoints = useRef<Point[]>([]);

  // ── Offscreen drawing cache ──
  const drawingCacheRef = useRef<OffscreenLayerCache | null>(null);

  function ensureDrawingCache(w: number, h: number): OffscreenLayerCache {
    let cache = drawingCacheRef.current;
    if (!cache) {
      const canvas = document.createElement('canvas');
      cache = { canvas, ctx: canvas.getContext('2d')!, valid: false, width: 0, height: 0, cam: { zoom: 1, offsetX: 0, offsetY: 0 } };
      drawingCacheRef.current = cache;
    }
    if (cache.width !== w || cache.height !== h) {
      cache.canvas.width = Math.round(w);
      cache.canvas.height = Math.round(h);
      cache.width = w;
      cache.height = h;
      cache.valid = false;
    }
    return cache;
  }

  const invalidateCache = useCallback(() => {
    if (drawingCacheRef.current) {
      drawingCacheRef.current.valid = false;
    }
  }, []);

  // ── measureText cache ──
  const measureCacheRef = useRef<Map<string, number>>(new Map());

  function cachedMeasureText(ctx: CanvasRenderingContext2D, text: string, font: string): number {
    const key = `${font}|${text}`;
    const cache = measureCacheRef.current;
    let width = cache.get(key);
    if (width === undefined) {
      ctx.font = font;
      width = ctx.measureText(text).width;
      cache.set(key, width);
      // Limit cache size
      if (cache.size > 500) cache.clear();
    }
    return width;
  }

  /** Render a single stroke onto a 2D context */
  function renderStrokeToCtx(ctx: CanvasRenderingContext2D, s: Stroke, cam: { zoom: number; offsetX: number; offsetY: number }) {
    ctx.save();
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.lineWidth * cam.zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (s.type === 'path' && s.points && s.points.length > 1) {
      ctx.beginPath();
      const p0 = worldToScreen(s.points[0].x, s.points[0].y, cam);
      ctx.moveTo(p0.x, p0.y);
      for (let i = 1; i < s.points.length; i++) {
        const p = worldToScreen(s.points[i].x, s.points[i].y, cam);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    } else if (s.type === 'rect') {
      const p1 = worldToScreen(s.x1!, s.y1!, cam);
      const p2 = worldToScreen(s.x2!, s.y2!, cam);
      ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
    } else if (s.type === 'circle') {
      const p1 = worldToScreen(s.x1!, s.y1!, cam);
      const p2 = worldToScreen(s.x2!, s.y2!, cam);
      const rx = Math.abs(p2.x - p1.x) / 2;
      const ry = Math.abs(p2.y - p1.y) / 2;
      ctx.beginPath();
      ctx.ellipse(p1.x + (p2.x - p1.x) / 2, p1.y + (p2.y - p1.y) / 2, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.type === 'line') {
      const p1 = worldToScreen(s.x1!, s.y1!, cam);
      const p2 = worldToScreen(s.x2!, s.y2!, cam);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  const startStroke = useCallback((wx: number, wy: number, tool: string) => {
    if (tool === 'draw') {
      currentStroke.current = {
        id: crypto.randomUUID(),
        type: 'path',
        points: [{ x: wx, y: wy }],
        color: color.current,
        lineWidth: lineWidth.current,
      };
    } else if (tool === 'shape') {
      currentStroke.current = {
        id: crypto.randomUUID(),
        type: shapeType.current as Stroke['type'],
        x1: wx, y1: wy, x2: wx, y2: wy,
        color: color.current,
        lineWidth: lineWidth.current,
      };
    } else if (tool === 'erase') {
      eraseAt(wx, wy);
    }
  }, []);

  const continueStroke = useCallback((wx: number, wy: number, tool: string) => {
    if (!currentStroke.current) {
      if (tool === 'erase') eraseAt(wx, wy);
      return;
    }
    if (currentStroke.current.type === 'path') {
      currentStroke.current.points!.push({ x: wx, y: wy });
    } else {
      currentStroke.current.x2 = wx;
      currentStroke.current.y2 = wy;
    }
    engine.markDirty('drawing');
  }, [engine]);

  const endStroke = useCallback(() => {
    if (currentStroke.current) {
      strokes.current.push(currentStroke.current);
      currentStroke.current = null;
      invalidateCache();
      engine.markDirty('drawing');
    }
  }, [engine, invalidateCache]);

  function eraseAt(wx: number, wy: number) {
    const eraseRadius = 20;
    // Find indices of strokes to erase (in reverse order to preserve indices during iteration)
    const toErase: number[] = [];
    for (let i = strokes.current.length - 1; i >= 0; i--) {
      const s = strokes.current[i];
      let hit = false;
      if (s.type === 'path') {
        hit = !!s.points?.some(p => dist(p.x, p.y, wx, wy) < eraseRadius);
      } else {
        const cx = (s.x1! + s.x2!) / 2;
        const cy = (s.y1! + s.y2!) / 2;
        hit = dist(cx, cy, wx, wy) < eraseRadius;
      }
      if (hit) toErase.push(i);
    }
    if (toErase.length > 0) {
      // Notify about erased strokes before removing (indices are valid at this point)
      for (const idx of toErase) {
        const stroke = strokes.current[idx];
        if (!stroke) continue;
        optsRef.current?.onEraseStroke?.(
          idx,
          {
            ...stroke,
            points: stroke.points?.map((point) => ({ ...point })),
          },
        );
      }
      // Remove in reverse-index order (toErase is already reverse-sorted)
      for (const idx of toErase) {
        strokes.current.splice(idx, 1);
      }
      invalidateCache();
      engine.markDirty('drawing');
    }
  }

  const startMeasure = useCallback((wx: number, wy: number, isPublic = false) => {
    // If a public ruler was already broadcast, hide it before starting a new one
    if (publicRulerSent.current) {
      opts?.onRulerHide?.();
      publicRulerSent.current = false;
    }
    measurePoints.current = [{ x: wx, y: wy }, { x: wx, y: wy }];
    measurePublic.current = isPublic;
    engine.markDirty('drawing');
  }, [engine, opts]);

  const updateMeasure = useCallback((wx: number, wy: number) => {
    if (measurePoints.current.length >= 2) {
      // Update the live cursor (last point)
      measurePoints.current[measurePoints.current.length - 1] = { x: wx, y: wy };
      engine.markDirty('drawing');
    }
  }, [engine]);

  /** Add a waypoint at the current position (keeps current cursor as next live end). */
  const addWaypoint = useCallback((wx: number, wy: number) => {
    if (measurePoints.current.length >= 2) {
      // Freeze the live cursor position as a fixed waypoint, then add a new live point
      measurePoints.current[measurePoints.current.length - 1] = { x: wx, y: wy };
      measurePoints.current.push({ x: wx, y: wy });
      engine.markDirty('drawing');
    }
  }, [engine]);

  const endMeasure = useCallback(() => {
    if (measurePoints.current.length >= 2) {
      // Use all points (including the live cursor which is the real end-point at this moment)
      const pts = measurePoints.current;
      const { total } = measurePath(
        pts.map(p => ({ x: Math.round(p.x / engine.gridSize), y: Math.round(p.y / engine.gridSize) })),
        measurementMode.current,
        gridSizeFt.current,
      );
      // Broadcast the ruler to other clients so everyone sees the measurement —
      // previously only a Shift+drag broadcast, so a plain measure was invisible
      // to other players (bug #5). Shift → persistent (stays until dismissed);
      // plain measure → ephemeral (receiver auto-hides after 5s). Guard on
      // total>0 so a click-without-drag doesn't spam a zero-length ruler.
      if (total > 0) {
        opts?.onRulerShow?.(pts, total, measurePublic.current);
        publicRulerSent.current = true;
      }
    }
    measurePoints.current = [];
    measurePublic.current = false;
    engine.markDirty('drawing');
  }, [engine, opts]);

  const clearAll = useCallback(() => {
    strokes.current = [];
    invalidateCache();
    engine.markDirty('drawing');
  }, [engine, invalidateCache]);

  // ── Token drag ruler ──

  const startTokenDragRuler = useCallback((wx: number, wy: number) => {
    tokenDragPoints.current = [{ x: wx, y: wy }, { x: wx, y: wy }];
    engine.markDirty('drawing');
  }, [engine]);

  const updateTokenDragRuler = useCallback((wx: number, wy: number) => {
    if (tokenDragPoints.current.length >= 2) {
      tokenDragPoints.current[tokenDragPoints.current.length - 1] = { x: wx, y: wy };
      engine.markDirty('drawing');
    }
  }, [engine]);

  /** Freeze the current live point as a fixed waypoint and add a fresh live end
   * — mirrors addWaypoint but for the token drag ruler (Q while dragging). */
  const addTokenDragWaypoint = useCallback((wx: number, wy: number) => {
    if (tokenDragPoints.current.length >= 2) {
      tokenDragPoints.current[tokenDragPoints.current.length - 1] = { x: wx, y: wy };
      tokenDragPoints.current.push({ x: wx, y: wy });
      engine.markDirty('drawing');
    }
  }, [engine]);

  const endTokenDragRuler = useCallback(() => {
    // Broadcast the completed move path so other players see how far the token
    // travelled (bug #5 follow-up). On-drop, not live: the token's position is
    // only committed to other clients on drop (drag mutates the local ref, no
    // WS send), so a live ruler would point at a token others still see at its
    // origin. Ephemeral (persistent=false → receiver auto-hides after 5s),
    // reusing the same ruler:show pipeline as the measure tool.
    const pts = tokenDragPoints.current;
    if (pts.length >= 2) {
      const { total } = measurePath(
        pts.map(p => ({ x: Math.round(p.x / engine.gridSize), y: Math.round(p.y / engine.gridSize) })),
        measurementMode.current,
        gridSizeFt.current,
      );
      if (total > 0) {
        opts?.onRulerShow?.(pts, total, false);
        publicRulerSent.current = true;
      }
    }
    tokenDragPoints.current = [];
    engine.markDirty('drawing');
  }, [engine, opts]);

  const showPublicRuler = useCallback((ruler: PublicRuler) => {
    // Replace any existing ruler from the same player
    publicRulers.current = publicRulers.current.filter(r => r.by !== ruler.by);
    publicRulers.current.push(ruler);
    engine.markDirty('drawing');
    // Persistent rulers (hideAt === 0) don't auto-hide
    if (ruler.hideAt > 0) {
      const delay = Math.max(0, ruler.hideAt - Date.now());
      setTimeout(() => {
        publicRulers.current = publicRulers.current.filter(r => r.by !== ruler.by || r.hideAt !== ruler.hideAt);
        engine.markDirty('drawing');
      }, delay);
    }
  }, [engine]);

  const hidePublicRuler = useCallback((by: string) => {
    publicRulers.current = publicRulers.current.filter(r => r.by !== by);
    engine.markDirty('drawing');
  }, [engine]);

  const clearPublicRulers = useCallback(() => {
    if (publicRulers.current.length > 0) {
      publicRulers.current = [];
      engine.markDirty('drawing');
    }
  }, [engine]);

  const renderDrawing = useCallback(() => {
    const canvas = engine.refs.current.drawing;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.width / dpr;
    const cssH = canvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cam = engine.cameraRef.current;
    ctx.clearRect(0, 0, cssW, cssH);

    // ── Offscreen cache for completed strokes ──
    const cache = ensureDrawingCache(canvas.width, canvas.height);

    if (!cache.valid) {
      const oc = cache.ctx;
      oc.setTransform(dpr, 0, 0, dpr, 0, 0);
      oc.clearRect(0, 0, cssW, cssH);
      const vp = getViewportBounds(cam, cssW, cssH);
      for (const s of strokes.current) {
        // Viewport culling for strokes
        if (s.type === 'path' && s.points && s.points.length > 0) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const p of s.points) {
            if (p.x < minX) minX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.x > maxX) maxX = p.x;
            if (p.y > maxY) maxY = p.y;
          }
          const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
          const rx = (maxX - minX) / 2 + 20, ry = (maxY - minY) / 2 + 20;
          if (!isInViewport(cx, cy, Math.max(rx, ry), vp)) continue;
        } else if (s.x1 !== undefined && s.y1 !== undefined && s.x2 !== undefined && s.y2 !== undefined) {
          const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2;
          const rx = Math.abs(s.x2 - s.x1) / 2 + 20, ry = Math.abs(s.y2 - s.y1) / 2 + 20;
          if (!isInViewport(cx, cy, Math.max(rx, ry), vp)) continue;
        }
        renderStrokeToCtx(oc, s, cam);
      }
      cache.cam = { ...cam };
      cache.valid = true;
    }

    // Draw from cache — apply transform if camera changed since cache was built
    const cc = cache.cam;
    if (cc.zoom === cam.zoom && cc.offsetX === cam.offsetX && cc.offsetY === cam.offsetY) {
      // Pixel-perfect blit (bypass DPR transform — cache is already at backing-store resolution)
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(cache.canvas, 0, 0);
      ctx.restore();
    } else {
      const s = cam.zoom / cc.zoom;
      const tx = (cam.offsetX - cc.offsetX * s) * dpr;
      const ty = (cam.offsetY - cc.offsetY * s) * dpr;
      ctx.save();
      ctx.setTransform(s, 0, 0, s, tx, ty);
      ctx.drawImage(cache.canvas, 0, 0);
      ctx.restore();
    }

    // Active stroke — draw directly (not cached)
    if (currentStroke.current) {
      renderStrokeToCtx(ctx, currentStroke.current, cam);
    }

    // Text objects — render after strokes, before ruler
    const textObjs = textObjectsRefInternal.current;
    if (textObjs && textObjs.length > 0) {
      ctx.save();
      const selTextId = selectedTextIdRefInternal.current;
      for (const tObj of textObjs) {
        const basePx = TEXT_FONT_SIZE_MAP[tObj.fontSize] ?? 20;
        const scaledPx = basePx * cam.zoom;
        const pos = worldToScreen(tObj.x, tObj.y, cam);
        ctx.save();
        if (tObj.rotation) {
          ctx.translate(pos.x, pos.y);
          ctx.rotate(tObj.rotation);
          ctx.translate(-pos.x, -pos.y);
        }
        ctx.font = `${scaledPx}px sans-serif`;
        ctx.fillStyle = tObj.color;
        ctx.textBaseline = 'top';
        ctx.fillText(tObj.text, pos.x, pos.y);

        // Selection highlight
        if (selTextId === tObj.id) {
          const tw = ctx.measureText(tObj.text).width;
          ctx.strokeStyle = '#2196F3';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([4, 3]);
          ctx.strokeRect(pos.x - 2, pos.y - 2, tw + 4, scaledPx + 4);
          ctx.setLineDash([]);
        }
        ctx.restore();
      }
      ctx.restore();
    }

    // Measure polyline
    const pts = measurePoints.current;
    if (pts.length >= 2) {
      const screenPts = pts.map(p => worldToScreen(p.x, p.y, cam));

      // Draw dashed line segments
      ctx.save();
      ctx.strokeStyle = '#00aaff';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(screenPts[0].x, screenPts[0].y);
      for (let i = 1; i < screenPts.length; i++) {
        ctx.lineTo(screenPts[i].x, screenPts[i].y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Endpoint dot at start
      ctx.fillStyle = '#00aaff';
      ctx.beginPath();
      ctx.arc(screenPts[0].x, screenPts[0].y, 4, 0, Math.PI * 2);
      ctx.fill();

      // Per-segment distance labels + waypoint dots
      const gridCells = engine.gridSize;
      const mode = measurementMode.current;
      const ftPerCell = gridSizeFt.current;
      const diagonalState = { count: 0 };
      let running = 0;

      const measureFont = '12px sans-serif';
      ctx.font = measureFont;

      for (let i = 0; i < screenPts.length - 1; i++) {
        const fromGrid = { x: Math.round(pts[i].x / gridCells), y: Math.round(pts[i].y / gridCells) };
        const toGrid = { x: Math.round(pts[i + 1].x / gridCells), y: Math.round(pts[i + 1].y / gridCells) };
        const segFt = measureDistance(fromGrid, toGrid, mode, ftPerCell, diagonalState);
        running += segFt;

        // Label at midpoint of each segment
        const isLastSeg = i === screenPts.length - 2;
        const midX = (screenPts[i].x + screenPts[i + 1].x) / 2;
        const midY = (screenPts[i].y + screenPts[i + 1].y) / 2;

        const ftUnit = tRef.current('ruler.ft');
        const segLabel = `${Math.round(segFt)} ${ftUnit}`;
        const tmWidth = cachedMeasureText(ctx, segLabel, measureFont);
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.fillRect(midX - tmWidth / 2 - 5, midY - 10, tmWidth + 10, 18);
        ctx.fillStyle = '#00aaff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(segLabel, midX, midY - 1);

        // Waypoint dot (not at the very first point)
        if (i > 0) {
          ctx.fillStyle = '#00aaff';
          ctx.beginPath();
          ctx.arc(screenPts[i].x, screenPts[i].y, 4, 0, Math.PI * 2);
          ctx.fill();
        }

        // Total label at end point when there are multiple segments
        if (isLastSeg && screenPts.length > 2) {
          const totalLabel = `${tRef.current('ruler.total')} ${Math.round(running)} ${ftUnit}`;
          const ttmWidth = cachedMeasureText(ctx, totalLabel, measureFont);
          const ex = screenPts[screenPts.length - 1].x;
          const ey = screenPts[screenPts.length - 1].y;
          ctx.fillStyle = 'rgba(0,0,0,0.75)';
          ctx.fillRect(ex - ttmWidth / 2 - 5, ey + 8, ttmWidth + 10, 18);
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(totalLabel, ex, ey + 17);
        }
      }

      ctx.restore();
    }

    // Public rulers from other players
    const now = Date.now();
    for (const ruler of publicRulers.current) {
      if (ruler.hideAt > 0 && ruler.hideAt <= now) continue;
      if (ruler.points.length < 2) continue;
      const rulerColor = ruler.color ?? '#ffaa00';
      const screenPts = ruler.points.map(p => worldToScreen(p.x, p.y, cam));
      ctx.save();
      ctx.strokeStyle = rulerColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(screenPts[0].x, screenPts[0].y);
      for (let i = 1; i < screenPts.length; i++) {
        ctx.lineTo(screenPts[i].x, screenPts[i].y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Endpoint dot at start
      ctx.fillStyle = rulerColor;
      ctx.beginPath();
      ctx.arc(screenPts[0].x, screenPts[0].y, 4, 0, Math.PI * 2);
      ctx.fill();

      // Per-segment distance labels + waypoint dots
      const gridCells = engine.gridSize;
      const mode = measurementMode.current;
      const ftPerCell = gridSizeFt.current;
      const pubDiagState = { count: 0 };
      let pubRunning = 0;

      const pubFont = '12px sans-serif';
      ctx.font = pubFont;

      for (let i = 0; i < screenPts.length - 1; i++) {
        const fromGrid = { x: Math.round(ruler.points[i].x / gridCells), y: Math.round(ruler.points[i].y / gridCells) };
        const toGrid = { x: Math.round(ruler.points[i + 1].x / gridCells), y: Math.round(ruler.points[i + 1].y / gridCells) };
        const segFt = measureDistance(fromGrid, toGrid, mode, ftPerCell, pubDiagState);
        pubRunning += segFt;

        const isLastSeg = i === screenPts.length - 2;
        const midX = (screenPts[i].x + screenPts[i + 1].x) / 2;
        const midY = (screenPts[i].y + screenPts[i + 1].y) / 2;

        const pubFtUnit = tRef.current('ruler.ft');
        const segLabel = `${Math.round(segFt)} ${pubFtUnit}`;
        const tmWidth = cachedMeasureText(ctx, segLabel, pubFont);
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.fillRect(midX - tmWidth / 2 - 5, midY - 10, tmWidth + 10, 18);
        ctx.fillStyle = rulerColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(segLabel, midX, midY - 1);

        // Waypoint dot
        if (i > 0) {
          ctx.fillStyle = rulerColor;
          ctx.beginPath();
          ctx.arc(screenPts[i].x, screenPts[i].y, 4, 0, Math.PI * 2);
          ctx.fill();
        }

        // Total label at end when multi-segment
        if (isLastSeg && screenPts.length > 2) {
          const totalLabel = `${tRef.current('ruler.total')} ${Math.round(pubRunning)} ${pubFtUnit}`;
          const ttmWidth = cachedMeasureText(ctx, totalLabel, pubFont);
          const ex = screenPts[screenPts.length - 1].x;
          const ey = screenPts[screenPts.length - 1].y;
          ctx.fillStyle = 'rgba(0,0,0,0.75)';
          ctx.fillRect(ex - ttmWidth / 2 - 5, ey + 8, ttmWidth + 10, 18);
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(totalLabel, ex, ey + 17);
        }
      }

      ctx.restore();
    }

    // Token drag ruler (green overlay while dragging)
    const tdPts = tokenDragPoints.current;
    if (tdPts.length >= 2) {
      const screenPts = tdPts.map(p => worldToScreen(p.x, p.y, cam));
      const dragColor = '#44cc44';
      ctx.save();
      ctx.strokeStyle = dragColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(screenPts[0].x, screenPts[0].y);
      for (let i = 1; i < screenPts.length; i++) {
        ctx.lineTo(screenPts[i].x, screenPts[i].y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Endpoint dot at start
      ctx.fillStyle = dragColor;
      ctx.beginPath();
      ctx.arc(screenPts[0].x, screenPts[0].y, 4, 0, Math.PI * 2);
      ctx.fill();

      // Distance label at end point
      const gridCells = engine.gridSize;
      const mode = measurementMode.current;
      const ftPerCell = gridSizeFt.current;
      const tdDiagState = { count: 0 };
      let tdRunning = 0;
      for (let i = 0; i < tdPts.length - 1; i++) {
        const fromGrid = { x: Math.round(tdPts[i].x / gridCells), y: Math.round(tdPts[i].y / gridCells) };
        const toGrid = { x: Math.round(tdPts[i + 1].x / gridCells), y: Math.round(tdPts[i + 1].y / gridCells) };
        tdRunning += measureDistance(fromGrid, toGrid, mode, ftPerCell, tdDiagState);
      }
      const tdLabel = `${Math.round(tdRunning)} ${tRef.current('ruler.ft')}`;
      const tdFont = '12px sans-serif';
      ctx.font = tdFont;
      const tdWidth = cachedMeasureText(ctx, tdLabel, tdFont);
      const endPt = screenPts[screenPts.length - 1];
      ctx.fillStyle = 'rgba(0,0,0,0.75)';
      ctx.fillRect(endPt.x - tdWidth / 2 - 5, endPt.y + 12, tdWidth + 10, 18);
      ctx.fillStyle = dragColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tdLabel, endPt.x, endPt.y + 21);
      ctx.restore();
    }
  }, [engine]);

  return {
    strokes,
    currentStroke,
    color,
    lineWidth,
    shapeType,
    measurementMode,
    gridSizeFt,
    measurePoints,
    tokenDragPoints,
    publicRulers,
    textObjectsRef: textObjectsRefInternal,
    selectedTextIdRef: selectedTextIdRefInternal,
    startStroke,
    continueStroke,
    endStroke,
    startMeasure,
    updateMeasure,
    addWaypoint,
    endMeasure,
    startTokenDragRuler,
    updateTokenDragRuler,
    addTokenDragWaypoint,
    endTokenDragRuler,
    showPublicRuler,
    hidePublicRuler,
    clearPublicRulers,
    clearAll,
    renderDrawing,
    invalidateCache,
    onRulerShow: opts?.onRulerShow,
    onRulerHide: opts?.onRulerHide,
  };
}
